import axios from 'axios';

/**
 * 阵容配置管理模块
 * 负责获取和管理阵容相关的配置枚举，以及数据转换
 */

// 配置枚举缓存
let configEnumsCache = null;

/**
 * 获取配置枚举（带缓存）
 * @returns {Promise<Object>} 配置枚举对象
 */
export async function getConfigEnums() {
    if (configEnumsCache) {
        return configEnumsCache;
    }

    const { data } = await axios.get('/v1/config/battleTeam');
    if (data.code === 200 && data.body?.configEnums) {
        configEnumsCache = data.body.configEnums;
        return configEnumsCache;
    }

    throw new Error('获取配置枚举失败');
}

/**
 * 清除配置缓存（用于强制刷新）
 */
export function clearConfigCache() {
    configEnumsCache = null;
}

/**
 * 根据枚举值获取名称
 * @param {Object} enumMap 枚举映射对象 { value: name }
 * @param {number} value 枚举值
 * @param {string} defaultPrefix 默认前缀（当找不到时使用）
 * @returns {string} 名称
 */
function getNameByValue(enumMap, value, defaultPrefix = '未知') {
    if (value === undefined || value === null) return null;
    if (value === 0) return enumMap[0] || '无';
    return enumMap[value] || `${defaultPrefix}(${value})`;
}

/**
 * 将 TeamDescription 转换为可显示的格式
 * @param {Object} team TeamDescription 对象
 * @param {Object} configEnums 配置枚举
 * @returns {Object} 转换后的对象（包含名称字段）
 */
export function formatTeamForDisplay(team, configEnums) {
    const { heroes, weaponTypes, artifactTypes, unifiedAttributes, pearlSkills } = configEnums;

    return {
        ...team,
        weaponName: team.weaponId !== undefined ? getNameByValue(weaponTypes, team.weaponId) : null,
        slots: Object.entries(team.slots).reduce((acc, [slotKey, slot]) => {
            acc[slotKey] = {
                ...slot,
                heroName: getNameByValue(heroes, slot.heroId),
                keyAttrNames: slot.keyAttrs?.map(attr => getNameByValue(unifiedAttributes, attr)) || [],
                artifactName:
                    slot.artifactId !== undefined ? getNameByValue(artifactTypes, slot.artifactId, '卸载') : null,
                pearlSkillName:
                    slot.pearlSkillId !== undefined ? getNameByValue(pearlSkills, slot.pearlSkillId, '无') : null,
            };
            return acc;
        }, {}),
    };
}

/**
 * 批量转换 TeamDescription 列表
 * @param {Array<Object>} teams TeamDescription 数组
 * @param {Object} configEnums 配置枚举
 * @returns {Array<Object>} 转换后的数组
 */
export function formatTeamsForDisplay(teams, configEnums) {
    return teams.map(team => formatTeamForDisplay(team, configEnums));
}

/**
 * 阵容数据管理类
 * 用于管理阵容的获取、缓存和显示转换
 * 为将来的编辑功能预留扩展接口
 */
export class TeamManager {
    constructor() {
        this.configEnums = null;
        this.teams = [];
    }

    /**
     * 初始化：获取配置枚举
     */
    async init() {
        this.configEnums = await getConfigEnums();
    }

    /**
     * 获取阵容列表
     * @returns {Promise<Array<Object>>} 原始 TeamDescription 数组
     */
    async fetchTeams() {
        const { data } = await axios.get('/v1/teams');
        if (data.code === 200 && data.body?.teams) {
            this.teams = data.body.teams;
            return this.teams;
        }
        throw new Error('获取阵容列表失败');
    }

    /**
     * 获取格式化后的阵容列表（用于显示）
     * @returns {Array<Object>} 转换后的阵容数组
     */
    getDisplayTeams() {
        if (!this.configEnums) {
            throw new Error('请先调用 init() 初始化配置');
        }
        return formatTeamsForDisplay(this.teams, this.configEnums);
    }

    /**
     * 获取原始阵容列表
     * @returns {Array<Object>} 原始 TeamDescription 数组
     */
    getRawTeams() {
        return this.teams;
    }

    /**
     * 根据ID获取阵容
     * @param {string} id 阵容ID
     * @returns {Object|null} TeamDescription 或 null
     */
    getTeamById(id) {
        return this.teams.find(t => t.id === id) || null;
    }

    /**
     * 创建新阵容
     * @param {Object} teamData TeamDescription 数据
     * @returns {Promise<Object>} 创建的阵容
     */
    async createTeam(teamData) {
        const { data } = await axios.post('/v1/teams', teamData);
        if (data.code === 200 && data.body?.team) {
            // 将新阵容添加到本地缓存
            this.teams.push(data.body.team);
            return data.body.team;
        }
        throw new Error(data.msg || '创建阵容失败');
    }

    /**
     * 更新阵容
     * @param {string} id 阵容ID
     * @param {Object} teamData TeamDescription 数据
     * @returns {Promise<Object>} 更新后的阵容
     */
    async updateTeam(id, teamData) {
        const { data } = await axios.put(`/v1/teams/${id}`, teamData);
        if (data.code === 200 && data.body?.team) {
            // 更新本地缓存中的阵容
            const index = this.teams.findIndex(t => t.id === id);
            if (index !== -1) {
                this.teams[index] = data.body.team;
            }
            return data.body.team;
        }
        throw new Error(data.msg || '更新阵容失败');
    }

    /**
     * 删除阵容
     * @param {string} id 阵容ID
     * @returns {Promise<boolean>} 是否成功
     */
    async deleteTeam(id) {
        const { data } = await axios.delete(`/v1/teams/${id}`);
        if (data.code === 200) {
            // 从本地缓存中移除阵容
            this.teams = this.teams.filter(t => t.id !== id);
            return true;
        }
        throw new Error(data.msg || '删除阵容失败');
    }

    /**
     * 刷新阵容列表（重新从服务器获取）
     * @returns {Promise<Array<Object>>} 最新的阵容列表
     */
    async refreshTeams() {
        return await this.fetchTeams();
    }

    /**
     * 加载并保存当前角色的预设阵容
     * 需要登录游戏服务器，耗时较长（约10-30秒）
     * @param {Array<number>} [presetIds] 可选，仅保存指定的预设阵容ID；不传则保存全部
     * @returns {Promise<Array<Object>>} 保存的预设阵容列表
     */
    async loadPresets(presetIds) {
        const payload = Array.isArray(presetIds) && presetIds.length > 0 ? { presetIds } : {};
        const { data } = await axios.post('/v1/teams/loadPresets', payload);
        if (data.code === 200) {
            await this.refreshTeams();
            return data.body?.teams || [];
        }
        throw new Error(data.msg || '加载预设阵容失败');
    }

    /**
     * 根据阵容ID复制一个阵容到当前角色
     * @param {string} teamId 被复制的阵容ID
     * @returns {Promise<Object>} 复制后的新阵容
     */
    async copyTeam(teamId) {
        const { data } = await axios.post('/v1/teams/copy', { teamId });
        if (data.code === 200 && data.body?.team) {
            this.teams.push(data.body.team);
            return data.body.team;
        }
        throw new Error(data.msg || '复制阵容失败');
    }

    /**
     * 在预设阵容基础上应用阵容配置
     * 需要登录游戏服务器，耗时较长（约10-30秒）
     * @param {string} teamId 阵容ID
     * @param {number} presetId 预设阵容槽位（1-5）
     * @returns {Promise<Object>} { overview, presetTeamName }：应用后的阵容概况结构化数据及服务器端权威预设阵容名称
     */
    async applyTeam(teamId, presetId) {
        const { data } = await axios.post('/v1/teams/apply', { teamId, presetId });
        if (data.code === 200) {
            return {
                overview: data.body?.overview || null,
                presetTeamName: data.body?.presetTeamName ?? '',
            };
        }
        throw new Error(data.msg || '应用阵容失败');
    }

    /**
     * 获取配置枚举（供外部组件使用）
     * @returns {Object|null} 配置枚举对象
     */
    getConfigEnums() {
        return this.configEnums;
    }
}

/**
 * 将枚举映射转为 Picker/Select 选项数组
 * @param {Object} enumMap { value: name } 映射
 * @param {Object} options 额外选项
 * @param {Array} options.prepend 前置选项，如 [{ value: undefined, text: '不操作' }]
 * @returns {Array<{value: number, text: string}>}
 */
export function buildPickerOptions(enumMap, options = {}) {
    const items = Object.entries(enumMap).map(([value, text]) => ({
        value: parseInt(value),
        text: String(text),
    }));
    if (options.prepend) {
        return [...options.prepend, ...items];
    }
    return items;
}
